import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { authRepository } from "@/infrastructure/auth/auth.repository";
import { useAuth } from "@/presentation/providers/auth.provider";
import { Button } from "@monorepo-apps/ui/src/components/button";
import { Input } from "@monorepo-apps/ui/src/components/input";
import { Label } from "@monorepo-apps/ui/src/components/label";

export const Route = createFileRoute("/login")({
  beforeLoad: async () => {
    const session = await authRepository.getSession();
    if (session) throw redirect({ to: "/" });
  },
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    const result = await signIn(email, password);
    setSubmitting(false);
    if (!result.ok) {
      toast.error(result.message ?? "Sign-in failed");
      return;
    }
    void navigate({ to: "/" });
  }

  return (
    <div className="bg-background flex min-h-screen w-full items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="mb-8">
          <div className="text-muted-foreground text-xs tracking-[0.18em] uppercase">Yango B2B</div>
          <h1 className="mt-1 text-lg font-medium tracking-tight">Assessment Tracker</h1>
          <p className="text-muted-foreground mt-2 text-xs">
            Sign in to log meetings, decisions, risks, and open questions.
          </p>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="email" className="text-xs">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="password" className="text-xs">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>
          <Button type="submit" disabled={submitting} className="mt-2">
            {submitting ? "Signing in…" : "Sign in"}
          </Button>
          <p className="text-muted-foreground mt-2 text-center text-xs">
            No account?{" "}
            <a href="/sign-up" className="underline-offset-2 hover:underline">
              Sign up
            </a>
          </p>
        </form>
      </div>
    </div>
  );
}
